import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { BotChat } from "@/components/landing/BotChat";
import { REVIEWS, BOT_CHATS } from "@/lib/site";

// Drag-to-scroll for the bot-chat strip: the scrollbar is hidden, so mouse
// users need another way to move it besides the trackpad/shift+wheel.
function useDragScroll() {
  const ref = useRef(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0 });
  const [dragging, setDragging] = useState(false);

  const onDown = (e) => {
    const el = ref.current;
    if (!el) return;
    drag.current = { down: true, startX: e.pageX, startScroll: el.scrollLeft };
    setDragging(true);
  };
  const stop = () => {
    drag.current.down = false;
    setDragging(false);
  };
  const onMove = (e) => {
    if (!drag.current.down || !ref.current) return;
    e.preventDefault();
    ref.current.scrollLeft = drag.current.startScroll - (e.pageX - drag.current.startX);
  };

  return {
    ref,
    dragging,
    handlers: {
      onMouseDown: onDown,
      onMouseLeave: stop,
      onMouseUp: stop,
      onMouseMove: onMove,
    },
  };
}

export const Reviews = () => {
  const { ref: trackRef, dragging, handlers } = useDragScroll();

  return (
    <section id="reviews" className="relative py-24 sm:py-32" data-testid="reviews-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <Reveal className="mb-14 max-w-2xl">
          <p className="mb-3 text-xs uppercase tracking-[0.25em] text-[#F0C674]/80">
            Глава 04 — Живые впечатления
          </p>
          <h2 className="font-serif text-4xl font-bold text-[#EDE6D3] sm:text-5xl lg:text-6xl">
            Примеры разборов и <span className="text-gold-gradient">отзывы</span>
          </h2>
          <p className="mt-4 text-[#EDE6D3]/60">
            Так выглядит настоящий диалог с Бабушкой Варварой в мессенджере MAX.
          </p>
        </Reveal>

        {/* Real-looking MAX bot conversations — drag with the mouse or swipe */}
        <div className="relative mb-16">
          <div
            ref={trackRef}
            {...handlers}
            className={`flex items-stretch gap-6 overflow-x-auto pb-4 select-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
              dragging ? "cursor-grabbing snap-none" : "cursor-grab snap-x"
            }`}
          >
            {BOT_CHATS.map((chat, i) => (
              <Reveal key={i} delay={i * 0.1} className={dragging ? "" : "snap-start"}>
                <BotChat chat={chat} testId={`bot-chat-${i}`} />
              </Reveal>
            ))}
          </div>
        </div>

        {/* Text reviews */}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {REVIEWS.map((r, i) => (
            <Reveal key={r.name} delay={(i % 3) * 0.08}>
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="flex h-full flex-col rounded-2xl border border-[#D4AF6E]/12 bg-surface p-6"
                data-testid={`review-card-${i}`}
              >
                <Quote className="h-7 w-7 text-[#D4AF6E]/60" />
                <p className="mt-3 flex-1 text-sm leading-relaxed text-[#EDE6D3]/75">
                  {r.text}
                </p>
                <div className="mt-5 flex items-center gap-3 border-t border-[#D4AF6E]/10 pt-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full gold-gradient font-serif text-lg font-bold text-[#0B0B1F]">
                    {r.name[0]}
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-[#EDE6D3]">{r.name}</span>
                    <span className="block text-xs text-[#EDE6D3]/50">{r.city}</span>
                  </span>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
